import { useGetMaxTakeProfitP } from '@ui/features/Trade/hooks/useGetMaxTakeProfitP';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { useGetPairMaxTpRatios } from '@ui/services/contract/hooks/TradingConfigFacet/useGetPairMaxTpRatios/useGetPairMaxTpRatios';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { getLeverageMarginByNotional } from '@ui/utils/trade/getLeverageMarginByNotional';
import {
  getMaxRemovableCollateral,
  MaxRemovableCollateralResult,
} from '@ui/utils/trade/getMaxRemovableCollateral';
import { getMaxTpRatios } from '@ui/utils/trade/getMaxTpRatios';
import { isSafeAfterMarginRemoval } from '@ui/utils/trade/isSafeAfterMarginRemoval';
import BigNumber from 'bignumber.js';
import { useCallback, useMemo } from 'react';

export const useMaxRemovableCollateral = ({
  position,
}: {
  position: Position;
}): MaxRemovableCollateralResult | undefined => {
  const pairInfos = useTradePairInfos();
  const pairInfo = useMemo(() => {
    return pairInfos?.find(
      (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
    );
  }, [pairInfos, position.pair.address]);

  const pairMarketPrice = usePriceLatest(position.pair.baseSymbol);
  const collateralMarketPrice = usePayTokenPriceLatest(position.collateralToken);
  const maxTakeProfitP = useGetMaxTakeProfitP();
  const { data: currentPairMaxTpRatios } = useGetPairMaxTpRatios({
    pairBase: position.pair.address,
  });

  // Match the contract: notional = qty × market price, not qty × entry price.
  const notionalUsd = useMemo(
    () => (pairMarketPrice ? position.size.times(pairMarketPrice) : undefined),
    [pairMarketPrice, position.size],
  );

  // Entry notional drives only the displayed leverage, kept in the same basis
  // as the position list / leverage-change rows.
  const entryNotionalUsd = useMemo(
    () => position.size.times(position.entryPrice),
    [position.size, position.entryPrice],
  );

  // Mirror `_marginLeverage` in TradingPortalFacet: tier picked by notional cap.
  const maxLeverage = useMemo(() => {
    if (!pairInfo || !notionalUsd) return 0;

    return (
      getLeverageMarginByNotional({
        leverageMargins: pairInfo.leverageMargins,
        notionalUsd,
      })?.maxLeverage ?? 0
    );
  }, [pairInfo, notionalUsd]);

  const getMaxTakeProfitPForLeverage = useCallback(
    (leverage: BigNumber) => {
      if (!currentPairMaxTpRatios || maxTakeProfitP === undefined) {
        return undefined;
      }

      return getMaxTpRatios({
        leverage: leverage.toNumber(),
        currentPairMaxTpRatios,
        maxTakeProfitP,
      });
    },
    [currentPairMaxTpRatios, maxTakeProfitP],
  );

  const isSafeAfterRemoval = useCallback(
    (collateralAfterRemoval: BigNumber) => {
      if (!pairInfo || !pairMarketPrice || !collateralMarketPrice) {
        return false;
      }

      return isSafeAfterMarginRemoval({
        position,
        pairInfo,
        pairMarketPrice,
        collateralMarketPrice,
        collateralAfterRemoval,
      });
    },
    [collateralMarketPrice, pairInfo, pairMarketPrice, position],
  );

  return useMemo(() => {
    if (
      maxLeverage <= 0 ||
      !collateralMarketPrice ||
      !notionalUsd ||
      notionalUsd.lte(0)
    ) {
      return undefined;
    }

    const hasLiquidationInputs =
      !!pairInfo && !!pairMarketPrice && !!collateralMarketPrice;

    return getMaxRemovableCollateral({
      collateral: position.collateral,
      notionalUsd,
      collateralPrice: collateralMarketPrice,
      maxLeverage,
      collateralDecimals: position.collateralToken.decimals,
      isSafeAfterRemoval: hasLiquidationInputs
        ? isSafeAfterRemoval
        : undefined,
      displayNotionalUsd: entryNotionalUsd,
      entryNotionalUsd,
      entryPrice: position.entryPrice,
      isLong: position.isLong,
      marketPrice: pairMarketPrice,
      getMaxTakeProfitPForLeverage,
    });
  }, [
    collateralMarketPrice,
    entryNotionalUsd,
    getMaxTakeProfitPForLeverage,
    isSafeAfterRemoval,
    maxLeverage,
    notionalUsd,
    pairInfo,
    pairMarketPrice,
    position.collateral,
    position.collateralToken.decimals,
    position.entryPrice,
    position.isLong,
  ]);
};
